/**
 * A canvas picture's front (Gabriel 2026-10-28). Printful's canvas print file can cover the front and the strip that
 * wraps around the stretcher, the same width on every side, with the art on the front only. Drawn on the front, the
 * whole file shrinks the art inside an empty border, so only the front is shown.
 *
 * From the picture's proportions (`pictureW` × `pictureH`, in pixels) and the canvas front (`w` × `h`, in inches), the
 * strip `m` solves (w + 2m) / (h + 2m) = pictureW / pictureH. Answers the share of the picture's width and height each
 * side's strip takes, or null when there is no strip worth cropping (a picture that already is the front).
 */
const LEAST_WRAP_IN = 0.1;

export function canvasWrap(pictureW: number, pictureH: number, w: number, h: number): { insetX: number; insetY: number } | null {
  if (!(pictureW > 0 && pictureH > 0 && w > 0 && h > 0) || pictureW === pictureH) return null;
  const m = (pictureW * h - pictureH * w) / (2 * (pictureH - pictureW));
  if (!(m >= LEAST_WRAP_IN)) return null;
  return { insetX: m / (w + 2 * m), insetY: m / (h + 2 * m) };
}

/** The picture's own style for its front: stretched to the front's box, clipped to the front, scaled back up to fill. */
export function canvasWrapStyle(wrap: { insetX: number; insetY: number }): { objectFit: 'fill'; clipPath: string; transform: string } {
  const pct = (n: number) => `${(n * 100).toFixed(4)}%`;
  return {
    objectFit: 'fill',
    clipPath: `inset(${pct(wrap.insetY)} ${pct(wrap.insetX)})`,
    transform: `scale(${(1 / (1 - 2 * wrap.insetX)).toFixed(6)}, ${(1 / (1 - 2 * wrap.insetY)).toFixed(6)})`,
  };
}
